import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Navbar from '../elements/Navbar';
import Form from '../components/Form';
import ResponseBlock from '../components/ResponseBlock';
import { BeatLoader } from 'react-spinners';

function ImageConverter() {
  const [userEmail, setUserEmail] = useState('');
  const [files, setFiles] = useState([]);
  const [imageOption, setImageOption] = useState('');
  const [downloadLink, setDownloadLink] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const email = localStorage.getItem("userEmail"); // ✅ Retrieve email from localStorage
    if (email) {
      setUserEmail(email);
    }
  }, []);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles(selectedFiles);
    setDownloadLink('');
    setMessage('');
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);

    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Clear input field
    }

    if (updatedFiles.length > 0) {
      const dataTransfer = new DataTransfer();
      updatedFiles.forEach(file => dataTransfer.items.add(file));
      fileInputRef.current.files = dataTransfer.files;
    }
  };

  const handleCancel = (event) => {
    event.preventDefault();
    setFiles([]);
    setDownloadLink('');
    setMessage('');

    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset input field
    }
  };

  const handleSubmit = async (event) => {
    
    if (imageOption.length === 0) {
      alert('Please choose what you want to do.');
      return;
    };

    if (files.length === 0) {
      alert('Please select at least one file before submitting.');
      return;
    };

    setIsLoading(true);
    setDownloadLink('');
    setMessage('');
    
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images[]', file); // Append multiple images
    });

    formData.append('email', userEmail);

    try {
      const baseUrl = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_API_URL_BASE_DEV : process.env.REACT_APP_API_URL_BASE_PROD;
      const response = await axios.post(`${baseUrl}/${imageOption}`, formData, {
        headers: {'Content-Type': 'multipart/form-data'},
        responseType: 'blob'
      });

      const contentType = response.headers['content-type'];
      
      if (contentType && contentType.includes('application/json')) {
        // Convert the blob to text and parse it as JSON to handle the error
        const text = await response.data.text();
        const errorJson = JSON.parse(text);
        if (errorJson.error) {
          setMessage(`Error: ${errorJson.error}`);
        }else{
          setMessage(`${errorJson.message}`);
        }
      } else {
        setTimeout(() => {
          // Create a download link for the WebP image
          const url = window.URL.createObjectURL(new Blob([response.data]));
          // Set the download link
          setDownloadLink(url);
          setMessage('Your converted images are ready for download.');
        }, 1500);
      }
    } catch (error) {
      console.error("There was an error uploading the images!", error);
      setMessage('An error occurred while processing the images.');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1450)
    }
  };

  return (
    <div>
      <Navbar />
      <div className="container mx-auto">
        <div className="updaterWrapper">
          <div className="space-y-12">
            <h1 className="text-2xl font-semibold leading-7 text-gray-900">Introduction</h1>
            <p>
              Google Lighthouse is a tool used to audit the performance of your website. It provides a score based on various factors, including image optimization. This application allows you to convert images to WebP format, which is a more efficient image format that can help improve your Lighthouse score.
            </p>
            <div className="border-b border-gray-900/10 pb-12">
              <h2 className="text-base font-semibold leading-7 text-gray-900">Upload Acceptable Files</h2>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Currently the Image Converter only accepts <span className="font-bold">JPEG</span>, <span className="font-bold">JPG</span>, and <span className="font-bold">PNG</span> files. The application will convert the images to WebP format. The converted images will be downloaded as a ZIP file. Please note that the application is still in development and may not work perfectly. If you encounter any issues, please contact Mario. The file name of the converted images will be the same as the uploaded file name, but with a .webp extension. For example, if you upload a file named <span className="font-bold">"example.jpg"</span>, the downloaded file will be named <span className="font-bold">"example.webp"</span>. The ZIP file will contain all the converted images in WebP format.
              </p>
            </div>
          </div>
          <div className="formWrapper mt-11">
            <Form onSubmit={handleSubmit}>
              <div className="grid grid-cols-6 border-b border-gray-900/10 pb-12">
                <div className="col-start-1 col-span-2 relative">
                  <label htmlFor="imageOption" className="block font-medium text-gray-700 mb-2">Select what you want to do.</label>
                  <select
                    onChange={(e) => setImageOption(e.target.value)}
                    value={imageOption}
                    name="imageOption"
                    className="py-3 outline-0 border-violet-800
                              mr-4 rounded-md font-semibold bg-violet-50 text-violet-700
                              hover:bg-violet-100"
                  >
                    <option value="" disabled defaultValue>I want to...</option>
                    <option value="format-images">Format Raw Product Images</option>
                    <option value="convert-images">Convert Marketing Images</option>
                  </select>
                </div>
                <div className="col-start-3 col-span-4 relative">
                  <label htmlFor="images" className="block font-medium text-gray-700 mb-2">Add your files.</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    name="images"
                    accept="image/jpeg, image/png, image/jpg"
                    multiple
                    onChange={handleFileChange}
                    className="w-full file:border-violet-800 text-slate-500 outline-0
                              file:mr-4 file:py-3 file:px-4
                              file:rounded-full file:border-0
                              file:text-sm file:font-semibold
                              file:bg-violet-50 file:text-violet-700
                              hover:file:bg-violet-100 mb-4">
                  </input>
                {files.length > 0 && (
                  <div className="col-start-3 col-span-4 mt-4 mb-4">
                    <h2 className="text-lg font-semibold mb-2">Selected Files:</h2>
                    <div className="overflow-x-auto overflow-y-auto max-h-[464pxpx]">
                      <table className="min-w-full border-collapse border border-gray-300">
                        <thead className="sticky">
                          <tr className="bg-gray-200">
                            <th className="border border-gray-300 px-4 py-2 text-left">File Name</th>
                            <th className="border border-gray-300 px-4 py-2 text-left">Size</th>
                            <th className="border border-gray-300 px-4 py-2 text-center">Remove</th>
                          </tr>
                        </thead>
                        <tbody>
                          {files.map((file, index) => (
                            <tr key={index} className="border-t">
                              <td className="border border-gray-300 px-4 py-2">{file.name}</td>
                              <td className="border border-gray-300 px-4 py-2">{(file.size / 1024).toFixed(2)} KB</td>
                              <td className="border border-gray-300 px-4 py-2 text-center">
                                <button
                                  onClick={() => handleRemoveFile(index)}
                                  className="text-red-500 hover:text-red-700"
                                  type="button"
                                  title="Remove file"
                                >
                                  X
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                </div>
                <div className="col-start-6 col-span-1 text-right space-x-3">
                  <button className="text-rose-700 font-semibold py-2 px-4 rounded" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button className="w-1/2 bg-indigo-500 text-white font-bold py-2 px-4 rounded-lg" 
                          type="submit"
                          disabled={isLoading}>
                    { isLoading ? <BeatLoader /> : 'Submit' }
                  </button>
                </div>
              </div>
            </Form>
          </div>
          <div className="responseWrapper">
            <ResponseBlock 
              message={message} 
              link={downloadLink} 
              fileName="converted_images"
              fileType="zip"
            />
          </div>
        </div>
      </div>  
    </div>
  );
}

export default ImageConverter;